import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { useRouter } from 'next/router';
import {
  IUser,
  Maybe,
} from '../../lib/graphql';

// --- Type Definitions ---

type IMeUser = Pick<IUser, 'id' | 'firstName' | 'lastName' | 'email' | 'profileImageUrl' | 'stripeCustomerId' | 'roles'> & {
  address?: Maybe<{
    __typename?: 'Address';
    line1: string;
    line2: string;
    city: string;
    county: string;
    country: string;
    postCode: string;
  }>;
};

type AuthContextType = {
  user: IMeUser | null | undefined;
  loading: boolean;
  errors: unknown[];
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
};

type IUserProviderProps = {
  children: ReactNode;
};

// --- Context Creation ---

const UserContext = createContext<AuthContextType | undefined>(undefined);

// --- User Provider ---

const UserProvider = ({ children }: IUserProviderProps): JSX.Element => {
  const { pathname } = useRouter();
  const [user, setUser] = useState<AuthContextType['user']>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<unknown[]>([]);

  // --- Helper Functions ---

  const handleApiError = (err: unknown) => {
    console.error(err);
    const errorMessage = err instanceof Error ? err.message : String(err);
    setErrors([errorMessage]);
  };

  const handleApiResponse = async (response: Response) => {
    console.log('LOGIN API RESPONSE', response);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'API request failed');
    }
    return response.json();
  };

  // --- Authentication Functions ---

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const resp = await handleApiResponse(response);
      console.log('LOGIN Error RESPONSE', resp.graphQLErrors);

      // Check for login success based on the API response structure
      // A successful login should have a user object and potentially a token
      // It should NOT have top-level errors or graphQLErrors related to the login mutation itself
      const loginSuccess = !!resp?.data?.login?.user && !resp.errors && (!resp.graphQLErrors || resp.graphQLErrors.length === 0);

      if (!loginSuccess) {
        // Construct a more specific error message if possible
        let errorMessage = 'Login failed. Please check your credentials.';
        if (resp.errors?.length > 0) {
          errorMessage = resp.errors.map((e: any) => e.message).join(' ');
          console.error('GraphQL Errors:', resp.errors);
          setErrors(resp.errors);
        } else if (resp.graphQLErrors?.length > 0) {
          errorMessage = resp.graphQLErrors.map((e: any) => e.message).join(' ');
          console.error('GraphQL Errors:', resp.graphQLErrors);
          setErrors(resp.graphQLErrors);
        }
        throw new Error(errorMessage);
      }

      // If login via API was successful, fetch the user data to update the context state
      await getUser(); // This updates the user state for the rest of the app

      // Optional: Add a check here to confirm getUser() actually populated the user state,
      // although the primary success indication is from the login API response itself.
      // This could help debug issues with the /api/auth/me endpoint if needed.
      // const currentUser = await getUser(); // Modify getUser to return the user or null
      // if (!currentUser) {
      //   console.warn('Login API succeeded, but getUser() failed to retrieve user data.');
      //   // Decide if this should still be considered a success for the login function's return value
      // }

      console.log('Login successful, user context updated.');
      return true; // Login is considered successful based on API response
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      await handleApiResponse(response);
      setUser(null);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const getUser = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/me', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const resp = await handleApiResponse(response);
      if (!resp.data) throw new Error('No user data returned');

      const { data } = resp;
      const profile = data.me?.user;
      setUser(profile || null);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    getUser();
  }, [pathname]);

  // --- Context Value ---

  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    loading,
    errors,
    login,
    logout,
  }), [user, loading, errors]);

  // --- Render ---

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

// --- Custom Hook ---

const useAuth = (): AuthContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useAuth must be used within a UserProvider');
  }
  return context;
};

export { UserProvider, useAuth };
